/**
 * ONE resolution per key, whatever the concurrency — a single-flight memo.
 *
 * WHY THIS EXISTS. Some resolutions are not idempotent from the outside: asking
 * twice does not hand back the same thing twice, it CREATES a second thing. A
 * document's inbox is exactly that — "open the inbox of this document" answers
 * with an address, and two callers racing each other end up with two addresses
 * for one document, so the side that watches one never sees what was deposited
 * in the other. Nothing about the calling code looks wrong: four independent
 * call sites, each perfectly reasonable on its own, all firing within the same
 * few hundred milliseconds.
 *
 * So the guarantee is not "we call it less often" (a cache) but "the application
 * can never be the reason a second one exists": while a resolution is in flight,
 * every other caller for the same key AWAITS THAT SAME PROMISE instead of
 * starting its own, and once it has settled they all read the one value.
 *
 * A REJECTION IS NOT MEMOIZED. It means UNKNOWN, never "there is none": every
 * caller waiting on it sees the failure, and the key is released so a later
 * caller genuinely retries rather than inheriting a permanent "no".
 *
 * The memo lives as long as the returned function does. For a per-session
 * resolution (a browser context is one identity for its whole life) that is the
 * intended lifetime: keep the function at module scope and the answer is settled
 * once for the session.
 */

export interface ResolveOnceHooks<Value> {
  /** A caller joined a resolution already in flight — nothing new was started. */
  onJoined?(key: string): void;
  /** A resolution completed and became the key's one answer. */
  onResolved?(key: string, value: Value): void;
}

export function resolveOncePerKey<Arg, Value>(
  keyOf: (arg: Arg) => string,
  resolve: (arg: Arg) => Promise<Value>,
  hooks: ResolveOnceHooks<Value> = {},
): (arg: Arg) => Promise<Value> {
  const byKey = new Map<string, Promise<Value>>();

  return (arg: Arg): Promise<Value> => {
    const key = keyOf(arg);
    const known = byKey.get(key);
    if (known) {
      hooks.onJoined?.(key);
      return known;
    }
    const resolving = resolve(arg).then(
      value => {
        hooks.onResolved?.(key, value);
        return value;
      },
      err => {
        // UNKNOWN, not "none" — release the key so a later caller can retry.
        byKey.delete(key);
        throw err;
      },
    );
    byKey.set(key, resolving);
    return resolving;
  };
}
